import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { gamificationAPI, shareAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  Award,
  Lock,
  Sparkles,
  Trophy,
  Users,
  MessageCircle,
  Zap,
  Crown,
  Star,
  Target,
  Share2,
} from 'lucide-react';

const badgeIcons = {
  footprints: Target,
  zap: Zap,
  crown: Crown,
  trophy: Trophy,
  users: Users,
  'message-circle': MessageCircle,
  star: Star,
};

export const Achievements = () => {
  const { t } = useTranslation();
  const [achievements, setAchievements] = useState([]);
  const [pointsHistory, setPointsHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shareDialog, setShareDialog] = useState(false);
  const [shareData, setShareData] = useState(null);
  const [sharingBadge, setSharingBadge] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [achievementsRes, pointsRes] = await Promise.all([
          gamificationAPI.getAchievements(),
          gamificationAPI.getPointsHistory(),
        ]);
        setAchievements(achievementsRes.data);
        setPointsHistory(pointsRes.data);
      } catch (error) {
        console.error('Failed to fetch achievements:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleShareBadge = async (badge) => {
    setSharingBadge(badge.id);
    try {
      const response = await shareAPI.getAchievementShare(badge.id);
      setShareData(response.data);
      setShareDialog(true);
    } catch (error) {
      toast.error('Failed to generate share link');
    } finally {
      setSharingBadge(null);
    }
  };

  const handleWhatsAppShare = () => {
    if (shareData?.whatsapp_url) {
      window.open(shareData.whatsapp_url, '_blank');
    }
  };

  const earnedBadges = achievements.filter(a => a.earned);
  const lockedBadges = achievements.filter(a => !a.earned);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-40" />)}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 pb-20 lg:pb-0" data-testid="achievements-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
            <Award className="w-8 h-8 text-purple-500" />
            {t('achievements.title', 'Achievements')}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t('achievements.subtitle', 'Collect badges and track your accomplishments')}
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-full gold-gradient">
                <Award className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('achievements.badgesEarned', 'Badges Earned')}</p>
                <p className="text-2xl font-bold">{earnedBadges.length}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-full bg-muted">
                <Lock className="w-5 h-5 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('achievements.locked', 'Locked')}</p>
                <p className="text-2xl font-bold">{lockedBadges.length}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 col-span-2 md:col-span-1">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-full bg-amber-500/10">
                <Sparkles className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('achievements.totalPoints', 'Total Points')}</p>
                <p className="text-2xl font-bold text-amber-500">
                  {pointsHistory.reduce((sum, p) => sum + p.amount, 0).toLocaleString()}
                </p>
              </div>
            </div>
          </Card>
        </div>

        <Tabs defaultValue="badges" className="w-full">
          <TabsList>
            <TabsTrigger value="badges" data-testid="tab-badges">{t('achievements.badges', 'Badges')}</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">{t('achievements.pointsHistory', 'Points History')}</TabsTrigger>
          </TabsList>

          <TabsContent value="badges" className="mt-6">
            {/* Earned Badges */}
            {earnedBadges.length > 0 && (
              <div className="mb-8">
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-amber-500" />
                  Earned ({earnedBadges.length})
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {earnedBadges.map((badge) => {
                    const Icon = badgeIcons[badge.icon] || Award;
                    return (
                      <Card 
                        key={badge.id} 
                        className="overflow-hidden badge-shine"
                        data-testid={`badge-${badge.id}`}
                      >
                        <CardContent className="p-6 text-center">
                          <div className="w-16 h-16 mx-auto rounded-full gold-gradient flex items-center justify-center mb-4">
                            <Icon className="w-8 h-8 text-white" />
                          </div>
                          <h3 className="font-semibold mb-1">{badge.name}</h3>
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {badge.description}
                          </p>
                          <Badge className="mt-3 bg-green-500/10 text-green-500 border-green-500/20">
                            Earned!
                          </Badge>
                          <Button
                            variant="outline"
                            size="sm"
                            className="mt-3 w-full"
                            onClick={() => handleShareBadge(badge)}
                            disabled={sharingBadge === badge.id}
                            data-testid={`share-badge-${badge.id}`}
                          >
                            <Share2 className="w-4 h-4 mr-2" />
                            Share
                          </Button>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Locked Badges */}
            {lockedBadges.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Lock className="w-5 h-5 text-muted-foreground" />
                  Locked ({lockedBadges.length})
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {lockedBadges.map((badge) => {
                    const Icon = badgeIcons[badge.icon] || Award;
                    return (
                      <Card 
                        key={badge.id} 
                        className="opacity-60"
                        data-testid={`badge-locked-${badge.id}`}
                      >
                        <CardContent className="p-6 text-center">
                          <div className="w-16 h-16 mx-auto rounded-full bg-muted flex items-center justify-center mb-4">
                            <Icon className="w-8 h-8 text-muted-foreground" />
                          </div>
                          <h3 className="font-semibold mb-1">{badge.name}</h3>
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {badge.description}
                          </p>
                          <Badge variant="outline" className="mt-3">
                            <Lock className="w-3 h-3 mr-1" />
                            Locked
                          </Badge>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}

            {achievements.length === 0 && (
              <Card className="p-12 text-center">
                <Award className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">No Badges Available</h3>
                <p className="text-muted-foreground">
                  Start learning to earn your first badge!
                </p>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="history" className="mt-6">
            <Card data-testid="points-history">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-500" />
                  Recent Points Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                {pointsHistory.length > 0 ? (
                  <div className="space-y-4">
                    {pointsHistory.map((transaction) => (
                      <div
                        key={transaction.id}
                        className="flex items-center justify-between p-4 rounded-lg bg-muted/50"
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-full bg-amber-500/10">
                            <Sparkles className="w-4 h-4 text-amber-500" />
                          </div>
                          <div>
                            <p className="font-medium">{transaction.description}</p>
                            <p className="text-sm text-muted-foreground">
                              {new Date(transaction.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <Badge className="gold-gradient text-white border-0">
                          +{transaction.amount}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Sparkles className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="font-semibold mb-2">No Points Yet</h3>
                    <p className="text-muted-foreground">
                      Complete courses and activities to earn points
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Share Dialog */}
        <Dialog open={shareDialog} onOpenChange={setShareDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Share2 className="w-5 h-5" />
                Share Your Achievement
              </DialogTitle>
            </DialogHeader>
            {shareData && (
              <div className="space-y-4">
                <div className="p-4 rounded-lg bg-muted text-center">
                  <Award className="w-12 h-12 mx-auto text-amber-500 mb-2" />
                  <h3 className="font-semibold">{shareData.badge?.name}</h3>
                  <p className="text-sm text-muted-foreground">{shareData.badge?.description}</p>
                </div>
                
                <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                  <p className="text-sm">{shareData.share_message}</p>
                </div>

                <Button
                  className="w-full bg-green-500 hover:bg-green-600"
                  onClick={handleWhatsAppShare}
                  data-testid="whatsapp-share-btn"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Share on WhatsApp
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default Achievements;
